/**
 * <ob-tiers-scroller>: on phones and tablets the tier cards swipe horizontally.
 * - On load, the recommended tier is centred (so "Huis" sits in the middle).
 * - The tab row above shows every tier's name and price; tapping one scrolls to its card.
 * - The active tab follows the swipe.
 * On desktop the cards sit in a 3-column grid and this does nothing visible.
 */
if (!customElements.get('ob-tiers-scroller')) {
  customElements.define(
    'ob-tiers-scroller',
    class ObTiersScroller extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-ob-tiers-list]');
        this.tabs = Array.from(this.querySelectorAll('[data-ob-tier-tab]'));
        if (!this.list) return;
        this.cards = Array.from(this.list.children);
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.scrollToCard(Number(tab.dataset.obTierTab), true));
        });

        let ticking = false;
        this.list.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => { this.updateActive(); ticking = false; });
        }, { passive: true });

        const start = this.cards.findIndex((c) => c.hasAttribute('data-ob-recommended'));
        // Wait a frame so layout (fonts, images) has settled before centring.
        requestAnimationFrame(() => this.scrollToCard(start >= 0 ? start : 0, false));
      }

      isSwipe() {
        return getComputedStyle(this.list).display === 'flex';
      }

      scrollToCard(index, smooth) {
        const card = this.cards[index];
        if (!card) return;
        // Hold the tapped tab while the list scrolls there; otherwise the scroll handler
        // re-highlights the card passing the centre and the tabs flicker.
        this.lockIndex = index;
        clearTimeout(this.lockTimer);
        this.lockTimer = setTimeout(() => { this.lockIndex = null; }, 1200);
        if (this.isSwipe()) {
          const left = card.offsetLeft - (this.list.clientWidth - card.clientWidth) / 2;
          this.list.scrollTo({ left, behavior: smooth && !this.reduced ? 'smooth' : 'auto' });
        }
        this.setActive(index);
      }

      updateActive() {
        const mid = this.list.scrollLeft + this.list.clientWidth / 2;
        let best = 0;
        let bestDist = Infinity;
        this.cards.forEach((card, i) => {
          const d = Math.abs(card.offsetLeft + card.clientWidth / 2 - mid);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        if (this.lockIndex != null) {
          // Programmatic scroll in progress: keep the tapped tab until its card is centred.
          if (best === this.lockIndex) { this.lockIndex = null; clearTimeout(this.lockTimer); }
          return;
        }
        this.setActive(best);
      }

      setActive(index) {
        this.tabs.forEach((tab, i) => {
          tab.classList.toggle('is-active', i === index);
          tab.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
      }
    }
  );
}
